<script setup lang="ts">
import { computed, onMounted, nextTick, useTemplateRef } from 'vue';
import type { FieldSchema } from '../../../types';
import { FILTER_MESSAGES, EMPTY_MESSAGES } from '@Constants';
import Dropdown from 'primevue/dropdown';

const emits = defineEmits<{
  change: [value: any];
  onDropdownSelectValue: [];
}>();

const props = withDefaults(
  defineProps<{
    clearIconStyle?: string;
    customClass?: string;
    disabled?: boolean;
    dontShowClearTrigger?: boolean;
    emptyMessage?: string;
    error?: string | boolean;
    field?: FieldSchema;
    hasFilter?: boolean;
    inputTextSize?: string;
    inputWidth?: string;
    isInlineEditing?: boolean;
    optionLabel?: string;
    optionValue?: string;
    options?: any[];
    placeholder?: string;
    showClear?: boolean;
  }>(),
  {
    clearIconStyle: '',
    dontShowClearTrigger: false,
    inputTextSize: 'text-sm',
    inputWidth: 'max-w-[290px] sm:max-w-[550px] xl:max-w-[620px]',
    isInlineEditing: false,
    placeholder: 'components.forms.placeholder.select',
  }
);

const model = defineModel();

const inlineComponentRef = useTemplateRef<InstanceType<typeof Dropdown> | null>('inlineComponentRef');

const settings = computed(() => props?.field?.settings);
const disabled = computed(() => props?.field?.state.disabled || props?.disabled);
const sanitizedOptions = computed(() => props?.field?.edit?.options || props?.options);
const showClearTrigger = computed(
  () => !props.dontShowClearTrigger && !props?.field?.settings?.required && !!model.value
);
const filteredOptions = computed(() =>
  sanitizedOptions.value?.filter((opt: any) => {
    if (opt === null || opt === undefined) return false;

    if (typeof opt === 'object' && 'value' in opt && opt.value === null) {
      return false;
    }

    return true;
  })
);
const options = computed(() => {
  const list =
    props?.field?.name === 'primary_color_map'
      ? props?.field?.edit?.options?.map((color: any) => color.value)
      : filteredOptions.value;

  if (!Array.isArray(list) || list.length === 0 || (list.length === 1 && list[0] == null)) return [];

  return list;
});

onMounted(() => checkEditType());

function checkEditType() {
  if (!props.isInlineEditing || !inlineComponentRef.value) return;

  nextTick(() => {
    if (!inlineComponentRef.value) return;
    inlineComponentRef.value.show();
  });
}

function onSelectValue(value: any) {
  props.isInlineEditing ? emits('onDropdownSelectValue') : emits('change', value);
}
</script>

<template>
  <div>
    <p
      v-if="(!options || options.length === 0) && field?.label && !props.isInlineEditing"
      class="py-1 text-xs italic text-gray-500"
    >
      {{ $t('components.emptyMessage.dropdownEmpty', { field: $t(field?.label || '') }) }}
    </p>

    <Dropdown
      v-bind="{ FILTER_MESSAGES, EMPTY_MESSAGES }"
      v-model="model"
      pt:item="text-sm"
      pt:filterInput="h-9 py-1 pl-3 text-sm"
      pt:emptymessage="text-sm"
      pt:item-label="overflow-hidden text-ellipsis max-w-[290px] sm:max-w-[550px] xl:max-w-[620px]"
      ref="inlineComponentRef"
      :disabled
      :options
      :class="['relative flex h-9 w-full justify-between', customClass]"
      :emptyFilterMessage="$t('components.emptyFilterMessage.dropdown')"
      :emptyMessage="$t(emptyMessage || 'components.emptyMessage.dropdown')"
      :emptySelectionMessage="$t('components.emptySelectionMessage.dropdown')"
      :filter="(hasFilter && options.length > 5) || options.length > 5"
      :filterMessage="$t('components.emptySelectionMessage.dropdown')"
      :filterResultMessage="$t('components.emptySelectionMessage.dropdown')"
      :invalid="!!error"
      :optionLabel="field?.edit?.label || optionLabel"
      :optionValue="field?.edit?.value || optionValue"
      :placeholder="$t(settings?.placeholder || placeholder)"
      :pt:clearIcon="clearIconStyle"
      :pt:trigger:class="{ 'text-red-600': !!error }"
      :pt:input:class="[
        inputTextSize,
        'flex items-center text-ellipsis overflow-hidden whitespace-nowrap',
        inputWidth,
        { 'af-field-error': !!error },
      ]"
      :showClear="showClearTrigger"
      :virtualScrollerOptions="options.length > 5 ? { itemSize: 38 } : undefined"
      @change="onSelectValue"
    />
  </div>
</template>
